/**
 * The implementation for our guide state storage.
 *
 * This is not for direct access. Instead, use the `#guide/guide-state-api`
 * module to interact with the guide state.
 */
import EventEmitter from "eventemitter3";

import type {GuideState} from "@repo/guide-types";
import {RequestInfo} from "@repo/shared-core";

import {DefaultGuideState} from "./default-guide-state.ts";
import {guideStateReducer} from "./guide-state-reducer.ts";

interface ChangeEventCallback<K extends keyof GuideState> {
    /**
     * Callback invoked when the guide state changes.
     *
     * @param fieldName The name of the field that changed, or undefined if
     * the entire state changed.
     */
    (fieldName: K): void;
}

/**
 * A class that manages state for the AI guide.
 */
export class GuideStateStorage {
    private _state: GuideState;
    private _eventEmitter = new EventEmitter();

    /**
     * Create a new guide state storage.
     *
     * @param initialState Optional initial state to use. All fields not given
     * will be set to their default values.
     */
    constructor(initialState?: Partial<GuideState>) {
        this._state = Object.freeze({
            ...DefaultGuideState,
            ...initialState,
        });
        this._eventEmitter = new EventEmitter();
    }

    private updateIfChanged(
        newState: GuideState,
        fieldName?: keyof GuideState,
    ): void {
        if (newState === this._state) {
            return;
        }

        this._state = Object.freeze(newState);
        this.emit("changed", fieldName);
    }

    private emit(eventName: "changed", fieldName?: keyof GuideState) {
        this._eventEmitter.emit(eventName, fieldName);
    }

    /**
     * Register a callback to be invoked when the state changes.
     *
     * @param eventName The name of the event to listen for.
     * @param callback The callback to invoke when the event is emitted.
     *
     * @returns A function that can be called to unregister the callback.
     */
    public on<K extends keyof GuideState>(
        eventName: "changed",
        callback: ChangeEventCallback<K>,
    ): () => void {
        this._eventEmitter.on(eventName, callback);
        return () => {
            this._eventEmitter.off(eventName, callback);
        };
    }

    /**
     * Set a value in the state.
     *
     * This will emit the `changed` event with the name of the changed field if
     * the new value actual differs from the old value.
     *
     * @param key The name of the field to set.
     * @param value The new value for the field, or a callback that takes the
     * current value of the field and returns a new value.
     */
    public set<K extends keyof GuideState>(
        key: K,
        value: GuideState[K] | ((currentValue: GuideState[K]) => GuideState[K]),
    ): void {
        if (key === "userAsStudent" && RequestInfo.isMobileApp) {
            // We don't allow changing the emulation in the mobile app
            // experience at this time.
            return;
        }
        const newState = guideStateReducer(this._state, {
            key,
            value,
        });
        this.updateIfChanged(newState, key);
    }

    /**
     * Get the current guide state.
     */
    public get current() {
        if (RequestInfo.isMobileApp && !this._state.userAsStudent) {
            // We want to override the userAsStudent emulation for mobile
            // app. We do this as late as possible, after any test setup has
            // modified the environment to emulate the mobile app. If we did
            // this in the constructor, we could end up trying to access the
            // initial request URL before it's available and that's a bigger
            // thing to fix.
            this._state = Object.freeze({
                ...this._state,
                userAsStudent: true,
            });
        }
        return this._state;
    }
}
